/**
 * SCOREBOARD-01 — data layer (Seth 2026-08-19: "Leaderboard, Tech stats, Plus
 * plans sold, positive online reviews… right now all of this is a manual
 * process. Something automatic would be nice.")
 *
 * Everything here is DERIVED from records that already exist:
 *   · Calls completed — v_dispatch_full (live ESC mirror), by tech
 *   · PLUS plans sold — mirror_serv_agr new agreements (by salesman empno)
 *     + Pegasus-native pegasus_plan_sale (by sold_by)
 *   · Review shout-outs — pegasus_review_shoutout (one-click office log;
 *     auto-pull from Google/Podium is a future hookup)
 *   · Invoiced $ — mirror_sales by salesman. HONESTY NOTE: the sales mirror
 *     currently lags (last invoice synced shows in `asOf`); the UI labels it.
 * No hand-kept spreadsheets: the board is right because the records are.
 */
import { useQuery } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type Period = "week" | "month";

const toISO = (d: Date) => d.toLocaleDateString("en-CA");

/** Monday-start week, calendar month. */
export function periodStart(p: Period): string {
  const now = new Date();
  if (p === "month") return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
  const dow = (now.getDay() + 6) % 7; // Mon=0
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
  return toISO(monday);
}

export interface TechRow {
  empno: string;
  name: string;
  dept: string | null;
  calls: number;
  plans: number;
  shoutouts: number;
  invoiced: number;
}

export interface ScoreboardData {
  rows: TechRow[];
  totals: { calls: number; plans: number; shoutouts: number; invoiced: number };
  invoicedAsOf: string | null; // max inv_date actually synced — the UI labels it
  start: string;
  shoutoutList: Shoutout[];
}

export interface Shoutout {
  id: string;
  empno: string | null;
  reviewer_name: string | null;
  source: string | null;
  stars: number | null;
  quote: string | null;
  review_date: string | null;
  created_at: string;
}

const DONE = (s: string | null) => {
  const t = (s ?? "").toLowerCase();
  return t.startsWith("comp") || t === "done";
};

export function useScoreboard(period: Period) {
  return useQuery({
    queryKey: ["scoreboard", period],
    refetchInterval: 60_000, // TV mode stays live
    queryFn: async (): Promise<ScoreboardData> => {
      const start = periodStart(period);
      const today = toISO(new Date());

      // Names for every empno we might meet (incl. inactive/office sellers).
      const [emps, disp, escPlans, pegPlans, shouts, maxInvRow] = await Promise.all([
        supabase.from("mirror_employee").select("empno, empname, firstname, lastname, dept").limit(500),
        supabase.from("v_dispatch_full").select("tech_empno, tech_name, tech_dept, status").gte("appointment_date", start).lte("appointment_date", today).limit(4000),
        supabase.from("mirror_serv_agr").select("salesman, beg_date").gte("beg_date", start).lte("beg_date", today).limit(2000),
        supabase.from("pegasus_plan_sale").select("sold_by, sold_at, status").gte("sold_at", start + "T00:00:00").limit(1000),
        supabase.from("pegasus_review_shoutout").select("*").gte("review_date", start).order("created_at", { ascending: false }).limit(200),
        supabase.from("mirror_sales").select("inv_date").order("inv_date", { ascending: false }).limit(1),
      ]);

      const nameOf = new Map<string, { name: string; dept: string | null }>();
      for (const e of emps.data ?? []) {
        const nm = (e.firstname ? `${e.firstname} ${e.lastname ?? ""}` : e.empname ?? e.empno).trim();
        nameOf.set(e.empno, { name: nm, dept: e.dept ?? null });
      }

      const rowsBy = new Map<string, TechRow>();
      const row = (empno: string, fallbackName?: string | null, fallbackDept?: string | null): TechRow => {
        let r = rowsBy.get(empno);
        if (!r) {
          const info = nameOf.get(empno);
          r = { empno, name: info?.name ?? fallbackName ?? empno, dept: info?.dept ?? fallbackDept ?? null, calls: 0, plans: 0, shoutouts: 0, invoiced: 0 };
          rowsBy.set(empno, r);
        }
        return r;
      };

      for (const d of disp.data ?? []) {
        if (!d.tech_empno || !DONE(d.status)) continue;
        row(d.tech_empno, d.tech_name, d.tech_dept).calls++;
      }
      for (const a of escPlans.data ?? []) {
        if (!a.salesman) continue;
        row(a.salesman).plans++;
      }
      for (const p of pegPlans.data ?? []) {
        if (!p.sold_by || p.status === "cancelled") continue;
        row(String(p.sold_by)).plans++;
      }
      const shoutoutList = (shouts.data ?? []) as Shoutout[];
      for (const s of shoutoutList) {
        if (s.empno) row(s.empno).shoutouts++;
      }

      // Invoiced $ — anchored to what the mirror actually HAS (it can lag).
      const invoicedAsOf: string | null = maxInvRow.data?.[0]?.inv_date ?? null;
      if (invoicedAsOf) {
        const anchor = new Date(invoicedAsOf + "T12:00:00");
        const salesStart = period === "month"
          ? toISO(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
          : toISO(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - 6));
        const { data: sales } = await supabase.from("mirror_sales").select("salesman, inv_amount").gte("inv_date", salesStart).lte("inv_date", invoicedAsOf).limit(8000);
        for (const s of sales ?? []) {
          if (!s.salesman) continue;
          row(s.salesman).invoiced += Number(s.inv_amount ?? 0);
        }
      }

      const rows = [...rowsBy.values()].sort((a, b) => b.calls - a.calls || b.plans - a.plans || b.invoiced - a.invoiced);
      const totals = rows.reduce(
        (t, r) => ({ calls: t.calls + r.calls, plans: t.plans + r.plans, shoutouts: t.shoutouts + r.shoutouts, invoiced: t.invoiced + r.invoiced }),
        { calls: 0, plans: 0, shoutouts: 0, invoiced: 0 },
      );
      return { rows, totals, invoicedAsOf, start, shoutoutList };
    },
  });
}

export function useAddShoutout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { empno: string; reviewer_name?: string; source?: string; stars?: number; quote?: string }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { error } = await supabase.from("pegasus_review_shoutout").insert({
        empno: v.empno, reviewer_name: v.reviewer_name ?? null, source: v.source ?? "Google",
        stars: v.stars ?? 5, quote: v.quote ?? null, created_by: who,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scoreboard"] }),
  });
}

export function useDeleteShoutout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_review_shoutout").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scoreboard"] }),
  });
}

// ── TV-SLIDES-01 (Seth 2026-08-21, from the break-room PowerPoint) ─────────

export interface ProductivityRow {
  empno: string;
  name: string;
  hours: number;
  dollars: number;
  /** ($/hr) / RHP — null when the tech has no pay basis recorded. */
  pct: number | null;
  weekLabel: string;
}

/** Weekly Service Productivity — the deck's "Hours · Total $ · %" slide,
 *  derived live for the LAST FULL WEEK (Mon–Sun) from ESC's per-ticket tech
 *  hours + the payroll pay basis (same math as Reports → Pay Week). */
export function useServiceProductivity() {
  return useQuery({
    queryKey: ["scoreboard_productivity"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<ProductivityRow[]> => {
      const now = new Date();
      const dow = (now.getDay() + 6) % 7;
      const thisMon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
      const mon = new Date(thisMon.getFullYear(), thisMon.getMonth(), thisMon.getDate() - 7);
      const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
      const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const weekLabel = `wk ending ${sun.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}`;

      const { data: sales, error: se } = await supabase
        .from("mirror_sales")
        .select("invoice, inv_amount, dept")
        .gte("inv_date", iso(mon)).lte("inv_date", iso(sun))
        .eq("dept", "03")
        .limit(2000);
      if (se) throw se;
      const byInvoice = new Map((sales ?? []).map((r) => [r.invoice as string, r]));
      const invoices = [...byInvoice.keys()];
      if (invoices.length === 0) return [];

      const emp: { invoice: string; empno: string | null; credit_pct: number | null; reg_hr: number | null; ot_hr: number | null }[] = [];
      for (let i = 0; i < invoices.length; i += 200) {
        const { data, error } = await supabase
          .from("mirror_sales_emp")
          .select("invoice, empno, credit_pct, reg_hr, ot_hr")
          .in("invoice", invoices.slice(i, i + 200));
        if (error) throw error;
        emp.push(...((data ?? []) as typeof emp));
      }
      const PSEUDO = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);
      const perInvoice = new Map<string, number>();
      for (const e of emp) if (e.empno && !PSEUDO.has(e.empno)) perInvoice.set(e.invoice, (perInvoice.get(e.invoice) ?? 0) + 1);

      const agg = new Map<string, { hours: number; dollars: number }>();
      for (const e of emp) {
        if (!e.empno || PSEUDO.has(e.empno)) continue;
        const inv = byInvoice.get(e.invoice);
        if (!inv) continue;
        const a = agg.get(e.empno) ?? { hours: 0, dollars: 0 };
        a.hours += (e.reg_hr ?? 0) + (e.ot_hr ?? 0);
        const n = perInvoice.get(e.invoice) ?? 1;
        a.dollars += e.credit_pct && e.credit_pct > 0 ? ((Number(inv.inv_amount) || 0) * e.credit_pct) / 100 : (Number(inv.inv_amount) || 0) / n;
        agg.set(e.empno, a);
      }
      const empnos = [...agg.keys()];
      if (empnos.length === 0) return [];
      // PAY-ONE-01: the RHP number only (pegasus_rhp_basis) — the scoreboard
      // is on the break-room TV, the pay rows themselves are private.
      const [{ data: emps }, { data: pay }] = await Promise.all([
        supabase.from("mirror_employee").select("empno, empname, firstname, lastname").in("empno", empnos),
        supabase.rpc("pegasus_rhp_basis", { p_empnos: empnos }),
      ]);
      const names = new Map(((emps ?? []) as { empno: string; empname: string | null; firstname: string | null; lastname: string | null }[])
        .map((e) => [e.empno, e.empname ?? (`${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() || e.empno)]));
      const rhp = new Map(((pay ?? []) as { empno: string; rhp: number | null }[])
        .map((r) => [r.empno, r.rhp == null ? null : Number(r.rhp)]));
      return empnos
        .map((e) => {
          const a = agg.get(e)!;
          const base = rhp.get(e) ?? null;
          const perHour = a.hours > 0 ? a.dollars / a.hours : 0;
          return {
            empno: e,
            name: names.get(e) ?? e,
            hours: Math.round(a.hours * 100) / 100,
            dollars: a.dollars,
            pct: base && a.hours > 0 ? (perHour / base) * 100 : null,
            weekLabel,
          };
        })
        .filter((r) => r.hours > 0)
        .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1));
    },
  });
}

export interface InstallLeaderRow {
  salesman: string;
  empno: string | null;
  name: string;
  count: number;
  avg: number;
  marginPct: number | null;
}

/** The deck's install-sales race: YTD equipment sales per salesman —
 *  count, average ticket, and gross margin vs the 45% goal. */
export function useInstallLeaders() {
  return useQuery({
    queryKey: ["scoreboard_install_leaders"],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<InstallLeaderRow[]> => {
      const yearStart = `${new Date().getFullYear()}-01-01`;
      const { data, error } = await supabase
        .from("mirror_sales")
        .select("invoice, inv_amount, labor_cost, mat_cost, other_cost, salesman, inv_type")
        .eq("dept", "01")
        .eq("inv_type", "Normal")
        .gte("inv_date", yearStart)
        .limit(5000);
      if (error) throw error;
      const agg = new Map<string, { count: number; billed: number; cost: number; costKnown: boolean }>();
      for (const r of (data ?? []) as { inv_amount: number | null; labor_cost: number | null; mat_cost: number | null; other_cost: number | null; salesman: string | null }[]) {
        const sm = (r.salesman ?? "").trim();
        if (!sm) continue;
        const a = agg.get(sm) ?? { count: 0, billed: 0, cost: 0, costKnown: false };
        a.count += 1;
        a.billed += Number(r.inv_amount) || 0;
        const c = (Number(r.labor_cost) || 0) + (Number(r.mat_cost) || 0) + (Number(r.other_cost) || 0);
        if (c > 0) a.costKnown = true;
        a.cost += c;
        agg.set(sm, a);
      }
      const sms = [...agg.keys()];
      if (sms.length === 0) return [];
      const { data: emps } = await supabase.from("mirror_employee").select("empno, empname, firstname, lastname").in("empno", sms);
      const names = new Map(((emps ?? []) as { empno: string; empname: string | null; firstname: string | null; lastname: string | null }[])
        .map((e) => [e.empno, e.empname ?? (`${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() || e.empno)]));
      return sms
        .map((sm) => {
          const a = agg.get(sm)!;
          return {
            salesman: sm,
            empno: names.has(sm) ? sm : null,
            name: names.get(sm) ?? sm,
            count: a.count,
            avg: a.count > 0 ? a.billed / a.count : 0,
            marginPct: a.costKnown && a.billed > 0 ? ((a.billed - a.cost) / a.billed) * 100 : null,
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 12);
    },
  });
}
